import { useIntl } from 'react-intl';

import { useToaster } from '@/components/base/toaster';
import { useJobPostingContext } from '@/contexts/job-posting-context';

import { useUpdateOpeningDescription } from '../job-details/use-update-opening-description';
import { usePoolForm } from '../pool-setup/use-pool-form';
import { JobPostingStep } from '../types';
import { getNextStep, JOB_POSTING_STEP_ORDER } from './step-config';

import type { SourcingOpeningUpdateParameters } from 'api-clients/monolith/models/SourcingOpeningUpdateParameters';

export function useValidatedStepper() {
  const intl = useIntl();
  const { notifyWarning } = useToaster();
  const {
    currentStep,
    setCurrentStep,
    hookForm,
    selectedChannelSlugs,
    selectedOpeningIds,
    markStepCompleted,
  } = useJobPostingContext();

  const { updateOpeningDescriptionAsync, isUpdatingOpening } =
    useUpdateOpeningDescription();
  const { validateOverlappingPoolPurchasesAsync, isValidatingPoolPurchases } =
    usePoolForm();

  const {
    trigger,
    formState: { errors },
    getValues,
    setValue,
  } = hookForm;

  const validateStep = async (step: JobPostingStep) => {
    /** Validate channels step */
    if (step === 'select-channels') {
      const isValid = selectedChannelSlugs.length > 0;

      if (!isValid) {
        notifyWarning({
          message: intl.formatMessage({
            defaultMessage: 'Please select at least one channel to proceed',
            description: 'Error message for no channels selected',
          }),
        });
        return false;
      }
    }

    /** Validate job details step */
    if (step === 'job-details') {
      const isValid = await trigger('jobDetails');

      if (!isValid) {
        const errorCount = Object.keys(errors?.jobDetails ?? {}).length ?? 0;

        notifyWarning({
          message: intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, =1 {You have one incomplete job. Please fill it in to continue.} other {You have {count} incomplete jobs. Please fill them in to continue.}}',
              description: 'Error message for incomplete job descriptions',
            },
            { count: errorCount },
          ),
        });
        return false;
      }

      // If there's only one opening, update its job description before moving to the next step
      if (selectedOpeningIds.length === 1) {
        const openingExternalId = selectedOpeningIds[0];

        const requestBody: SourcingOpeningUpdateParameters = {
          description: getValues(`jobDetails.${openingExternalId}.description`),
          state_code: getValues(`jobDetails.${openingExternalId}.stateCode`),
        };

        try {
          await updateOpeningDescriptionAsync({
            openingExternalId,
            requestBody,
          });
        } catch {
          // Error is already handled by the mutation's onError callback
          return false;
        }
      }
    }

    /** Validate pool step */
    if (step === 'pool-setup') {
      const isValid = await trigger('channels.pool');

      if (!isValid) {
        notifyWarning({
          message: intl.formatMessage({
            defaultMessage: 'Please complete the pool setup to proceed',
            description: 'Error message for incomplete pool setup',
          }),
        });
        return false;
      }

      const noConflicts = await validateOverlappingPoolPurchasesAsync();
      if (noConflicts) {
        // Mark the pool channel entry as validated so campaign-details step
        // can confirm all channels have been saved/validated.
        setValue('channels.pool.isValidated', true);
      }
      return noConflicts;
    }

    /** Validate channel forms step */
    if (step === 'campaign-details') {
      const isValid = await trigger('channels');

      if (!isValid) {
        /** Validate channel form errors */
        notifyWarning({
          message: intl.formatMessage({
            defaultMessage:
              'Please complete the spend details to proceed to payment.',
            description: 'Error message for incomplete spend details',
          }),
        });
        return false;
      }

      const channels = getValues('channels');

      /** Validate if at least one channel is selected */
      if (Object.keys(channels).length === 0) {
        notifyWarning({
          message: intl.formatMessage({
            defaultMessage: 'Please select at least one channel to proceed',
            description: 'Error message for no channels selected',
          }),
        });
        return false;
      }

      /** Validate if channel forms are saved and validated */
      const isAllChannelsValidated = Object.values(channels).every(
        channel => channel.isValidated,
      );

      if (!isAllChannelsValidated) {
        notifyWarning({
          message: intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, =1 {Please save the channel form before proceeding.} other {Please save all channel forms before proceeding.}}',
              description: 'Error message for incomplete channel forms',
            },
            { count: selectedChannelSlugs.length },
          ),
        });
        return false;
      }
    }

    return true;
  };

  /* Handle next step button click */
  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (!isValid) return;

    markStepCompleted(currentStep);
    const nextStep = getNextStep(currentStep, selectedChannelSlugs);
    if (nextStep !== null) setCurrentStep(nextStep);
  };

  /* Handle when users navigate to a step directly from the stepper */
  const handleStepClick = async (toStep: JobPostingStep) => {
    if (JOB_POSTING_STEP_ORDER[toStep] <= JOB_POSTING_STEP_ORDER[currentStep]) {
      setCurrentStep(toStep);
      return;
    }

    // Sequential validation is intentional - we validate steps in order and
    // stop at the first invalid step so the user sees a single error message.
    let step = currentStep;
    /* eslint-disable no-await-in-loop */
    while (step !== toStep) {
      const isValid = await validateStep(step);
      if (!isValid) {
        setCurrentStep(step);
        return;
      }

      markStepCompleted(step);
      const next = getNextStep(step, selectedChannelSlugs);
      // `next` being null or past `toStep` means `toStep` is unreachable from
      // the current configuration (e.g. it was filtered out by isStepVisible).
      // The breadcrumb hides such steps, so this is purely defensive.
      if (
        next === null ||
        JOB_POSTING_STEP_ORDER[next] > JOB_POSTING_STEP_ORDER[toStep]
      ) {
        setCurrentStep(step);
        return;
      }

      step = next;
    }
    /* eslint-enable no-await-in-loop */

    setCurrentStep(toStep);
  };

  return {
    handleNext,
    handleStepClick,
    validateStep,
    isLoading: isUpdatingOpening || isValidatingPoolPurchases,
  };
}
